import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import ButtonMenu from 'components/buttons/ButtonMenu';
import MenuItem from 'components/Menu/MenuItem';
import Input from 'components/forms/Input';
import Button from 'components/buttons/Button';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import TableRow from 'components/tables/tableRow';
import Checkbox from 'components/forms/Checkbox';
import AlertOutlineIcon from 'mdi-react/AlertOutlineIcon';
import NoResults from 'components/NoResults';
import Switch from 'components/forms/Switch';
import { debounce } from 'lodash';

import { resultsToOptions, uuidv4 } from 'utils/misc';
import { MODAL_TYPES } from 'constants/sequenceContact';
import { getErrorsCsv } from 'utils/contact';
import {
  destroySequenceContact, updateSequenceContact,
  removeFromCampaignSequenceContact, removeBulkFromCampaignSequenceContact,
  updateBulkSequenceContact, destroyBulkSequenceContact,
} from 'redux/actions/sequenceContactActions';
import { deleteContact, getPreviewContacts, searchPreviewContacts } from 'redux/actions/contactActions';
import { updateSequence, getSequences } from 'redux/actions/sequenceActions';
import {
  createAccountBlacklistedContact, createBulkAccountBlacklistedContacts,
} from 'redux/actions/accountBlacklistedContactActions';
import withContactList from 'components/ContactList/withContactList';
import Pagination from 'components/pagination/Pagination';

import ActionsModal from '../SequenceContactList/ActionsModal';
import ActionsBulkModal from '../SequenceContactList/ActionsBulkModal';
import ContactsLoader from '../ContactsLoader';
import ContactRow from './ContactRow';


const styles = () => ({
  switchWrapper: {
    display: 'inline-block',
    marginRight: '1rem',
  },
});

class SequenceContactList extends Component {
  static propTypes = {
    sequence: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      account_id: PropTypes.number,
      campaign: PropTypes.number,
    }).isRequired,
    sequences: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
    contacts: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    getSequences: PropTypes.func.isRequired,
    updateSequence: PropTypes.func.isRequired,
    showDuplicates: PropTypes.bool,
    showDownloadErrors: PropTypes.bool,
    showSteps: PropTypes.bool,
    showContactReplyForCurrentSequence: PropTypes.bool,
    getSelectedContactsOnPage: PropTypes.func.isRequired,
    handleSelectClick: PropTypes.func.isRequired,
    showNotification: PropTypes.func.isRequired,
    untoggleSelectedContactOnPage: PropTypes.func.isRequired,
    isSelected: PropTypes.func.isRequired,
    isAllChecked: PropTypes.func.isRequired,
    isIndeterminate: PropTypes.func.isRequired,
    handleSelectAll: PropTypes.func.isRequired,
    deleteContact: PropTypes.func.isRequired,
    getPreviewContacts: PropTypes.func.isRequired,
    validContacts: PropTypes.number,
    searchPreviewContacts: PropTypes.func.isRequired,
    classes: PropTypes.element.isRequired,
  };

  static defaultProps = {
    showDuplicates: true,
    showDownloadErrors: true,
    showContactReplyForCurrentSequence: false,
    showSteps: false,
    validContacts: 0,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedIds: new Set(),
      isModalOpen: false,
      isBulkModalOpen: false,
      whichModal: null, // defines which type of modal is open
      // seqContact for modals' actions
      whichSeqContact: { id: null, email: null },
      sequenceToMove: null, // defines to which sequence sequenceContact will be moved
    };
    this.parentComponentId = uuidv4(); // preventing duplicated popover IDs when reusing component multiple times
    // we will check for difference in selectedText
    this.toggleModal = this.toggleModal.bind(this);
    this.toggleBulkModal = this.toggleBulkModal.bind(this);

    // will be used to hide all popovers (dirty hack)
    // it will allow us to open 'areYouSure' type modal and at the same time close all 'legacy' type popovers
    this.mainModalRef = React.createRef();
  }

  toggleBulkModal(isOpen, whichModal = null) {
    this.setState({ isBulkModalOpen: isOpen });
    if (whichModal) {
      this.setState({ whichModal });
    }
    // nullify modal state values
    if (this.state.sequenceToMove) {
      this.setState({ sequenceToMove: null });
    }
  }

  toggleModal(isOpen, whichModal = null, seqContact = null) {
    // hide all popovers
    this.mainModalRef.current.click();
    this.setState({ isModalOpen: isOpen });
    if (whichModal) {
      this.setState({ whichModal });
    }
    if (seqContact) {
      this.setState({ whichSeqContact: seqContact });
    }
    // nullify modal state values
    if (this.state.sequenceToMove) {
      this.setState({ sequenceToMove: null });
    }
  }


  handleChangeSequence = (sequence) => {
    const sequenceToMove = this.props.sequences.results.filter(seq => seq.id === sequence.value)[0];
    this.setState({ sequenceToMove });
  }

  handleLoadSequenceOptions = async (filterString) => {
    await this.props.getSequences(this.props.sequence.campaign, { search: filterString });
    return resultsToOptions(this.props.sequences.results, 'name') || [];
  }

  handleAllowDuplicates = async () => {
    const { sequence, contacts } = this.props;
    await this.props.updateSequence(sequence.id, { allow_duplicates: !sequence.allow_duplicates });
    this.props.getPreviewContacts(contacts.params);
  }

  handleValidateContacts = async () => {
    const { sequence } = this.props;
    await this.props.updateSequence(sequence.id, {
      validate_contacts: !sequence.validate_contacts,
      send_to_risky: false,
    });
  }

  handleSendToRisky = async () => {
    const { sequence } = this.props;
    await this.props.updateSequence(sequence.id, { send_to_risky: !sequence.send_to_risky });
  }

  handleModalSuccess = async () => {
    if (this.state.whichModal === MODAL_TYPES.DELETE_MODAL) {
      this.props.deleteContact(this.state.whichSeqContact.id);
    }
    this.toggleModal(false);
    // show notifications
    if (this.state.whichModal === MODAL_TYPES.DELETE_MODAL) {
      this.props.showNotification(`${this.state.whichSeqContact.email} was deleted`, 'success');
    }
  }

  handleBulkModalSuccess = async () => {
    const selectedContactsOnPage = this.props.getSelectedContactsOnPage();
    if (this.state.whichModal === MODAL_TYPES.DELETE_MODAL) {
      const objectsListToRemove = selectedContactsOnPage.map(obj => ({
        id: obj.id,
      }));
      objectsListToRemove.map(obj => (this.props.deleteContact(obj.id)));
    }

    this.toggleBulkModal(false);
    // untoggle selected contact from being selected
    this.props.untoggleSelectedContactOnPage();
    // show notifications
    if (this.state.whichModal === MODAL_TYPES.DELETE_MODAL) {
      this.props.showNotification(`${selectedContactsOnPage.length} contacts were successfully deleted`, 'success');
    }
  }

  emitChangeDebounced = debounce(search => this.props.searchPreviewContacts({ search }), 300)

  renderContacts(contacts) {
    return (
      contacts.map((contact) => {
        const isSelected = this.props.isSelected(contact.id);
        const { handleSelectClick, showSteps, showContactReplyForCurrentSequence } = this.props;
        return (
          <ContactRow
            key={`contact-row-${contact.id}`}
            contact={contact}
            isSelected={isSelected}
            parentComponentId={this.parentComponentId}
            handleSelectClick={handleSelectClick}
            showSteps={showSteps}
            showContactReplyForCurrentSequence={showContactReplyForCurrentSequence}
            toggleModal={this.toggleModal}
            sequence={this.props.sequence}
          />
        );
      })
    );
  }

  render() {
    const {
      contacts, sequence, showDuplicates, classes,
      showDownloadErrors, showSteps, validContacts,
    } = this.props;
    const {
      isModalOpen, whichModal, whichSeqContact,
      sequenceToMove, isBulkModalOpen, selectedIds,
    } = this.state;
    const isLoading = contacts.loading || sequence.loading;
    const isSelectedOnPage = this.props.getSelectedContactsOnPage().length > 0;
    const contactsWithErrors = contacts.results.length - validContacts;
    return (
      <div className="sequence-contact-list w-100">
        <ActionsModal
          isModalOpen={isModalOpen}
          whichModal={whichModal}
          whichSeqContact={whichSeqContact}
          sequenceToMove={sequenceToMove}
          toggleModal={this.toggleModal}
          handleChangeSequence={this.handleChangeSequence}
          handleLoadSequenceOptions={this.handleLoadSequenceOptions}
          handleModalSuccess={this.handleModalSuccess}
        />
        <ActionsBulkModal
          isBulkModalOpen={isBulkModalOpen}
          getSelectedContactsOnPage={this.props.getSelectedContactsOnPage}
          sequenceToMove={sequenceToMove}
          whichModal={whichModal}
          toggleBulkModal={this.toggleBulkModal}
          selectedIds={selectedIds}
          handleChangeSequence={this.handleChangeSequence}
          handleLoadSequenceOptions={this.handleLoadSequenceOptions}
          handleBulkModalSuccess={this.handleBulkModalSuccess}
        />
        <div className="card__title" ref={this.mainModalRef}>
          {showDuplicates &&
          <React.Fragment>
            <h5 className="bold-text">Contacts</h5>
            <div className={classes.switchWrapper}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <label className="mt-0">
                {/* eslint-enable jsx-a11y/label-has-for */}
                <Switch
                  size="small"
                  onChange={this.handleAllowDuplicates}
                  checked={sequence.allow_duplicates}
                />
                Allow duplicates
              </label>
            </div>
            <div className={classes.switchWrapper}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <label className="mt-0">
                {/* eslint-enable jsx-a11y/label-has-for */}
                <Switch
                  size="small"
                  onChange={this.handleValidateContacts}
                  checked={sequence.validate_contacts}
                />
                Run Email Validation
              </label>
            </div>
            {sequence.validate_contacts &&
              <div className={classes.switchWrapper}>
                {/* eslint-disable jsx-a11y/label-has-for */}
                <label className="mt-0">
                  {/* eslint-enable jsx-a11y/label-has-for */}
                  <Switch
                    size="small"
                    onChange={this.handleSendToRisky}
                    checked={sequence.send_to_risky}
                  />
                  {"Allow sending to 'risky' contacts"}
                </label>
              </div>
            }
          </React.Fragment>
          }
        </div>
        <div className="actions">
          <div className="actions__left">
            <div className="mr-2 search-input-form">
              <Input
                inputProps={{ type: 'search' }}
                rightIcon="search"
                onChange={e => this.emitChangeDebounced(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>

          <div className="actions__right">
            {/* bulk actions will work with selected contacts on the current page only */}
            {isSelectedOnPage &&
              <ButtonMenu btnText="Bulk actions">
                <MenuItem onClick={() => this.toggleBulkModal(true, MODAL_TYPES.DELETE_MODAL)}>
                  Delete
                </MenuItem>
              </ButtonMenu>
            }

            {showDownloadErrors &&
            <div>
              <Button
                color="accent3"
                variant="contained"
                className="ml-2"
                onClick={() => getErrorsCsv(contacts.results, sequence)}
                disabled={isLoading || !contactsWithErrors > 0}
                startIcon={<AlertOutlineIcon />}
              >
                {isLoading ?
                  <span className="loading-dots">Download errors</span> :
                  <React.Fragment>Download errors ({contactsWithErrors})</React.Fragment>
                }
              </Button>
            </div>
            }

          </div>
        </div>

        <div className="mt-4 overflow-auto">
          <Table className="volt-table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={this.props.isIndeterminate()}
                    checked={this.props.isAllChecked()}
                    onChange={this.props.handleSelectAll}
                  />
                </TableCell>
                <TableCell>Full name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Outlet</TableCell>
                <TableCell>Contact Subject</TableCell>
                {showSteps && <TableCell />}
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>

              {(isLoading) && <ContactsLoader />}
              {(!isLoading) &&
                this.renderContacts(contacts.displayResults)
              }

              <tr className="material-table__row placeholder placeholder--invisible">
                {/* will help to hold the width of modal when no rows present */}
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
              </tr>

            </TableBody>
          </Table>
          <NoResults loading={isLoading} results={contacts.results} />
        </div>
        <Pagination
          items={{
            results: contacts.displayResults,
            count: contacts.previewParams.count,
            next: contacts.next,
            previous: contacts.previous,
            page: contacts.previewParams.page,
          }}
          pageSize={contacts.previewParams.pageSize}
          onChangePage={this.props.searchPreviewContacts}
          params={contacts.previewParams}
        />
      </div>
    );
  }
}


export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      sequences: state.sequences,
    }),
    {
      deleteContact,
      getSequences,
      updateSequence,
      updateSequenceContact,
      destroySequenceContact,
      updateBulkSequenceContact,
      destroyBulkSequenceContact,
      createAccountBlacklistedContact,
      removeFromCampaignSequenceContact,
      createBulkAccountBlacklistedContacts,
      removeBulkFromCampaignSequenceContact,
      getPreviewContacts,
      searchPreviewContacts,
    },
  ),
)(withContactList(SequenceContactList));
